// The latest responses across every survey in the workspace — a glance, not
// a browse (the server caps it at 10; paging through everything is the
// responses table's job on the survey's own Responses page). Each row links
// to that survey's Responses — filtered to the respondent when the host sent
// one.

import { Link } from "@tanstack/react-router";
import type { RecentResponse } from "@/api/types";
import { SectionCard } from "@/components/section-page/section-card";
import { ALL_TIME_KEY } from "@/features/time/time-chip";
import { timeAgo } from "./overview-mapping";

export interface RecentFeedProps {
    workspaceId: string;
    recent: RecentResponse[];
}

function scoreLabel(response: RecentResponse): string {
    if (response.score === null) return "—";
    return response.maxScore === null ? `${response.score}` : `${response.score}/${response.maxScore}`;
}

/** A known respondent opens their responses, all of them; otherwise the survey's list as it opens. */
function respondentSearch(response: RecentResponse): Record<string, string> {
    return response.respondentRef === null ? {} : { respondentRef: response.respondentRef, [ALL_TIME_KEY]: "all" };
}

export function RecentFeed({ workspaceId, recent }: RecentFeedProps) {
    return (
        <SectionCard id="overview-recent" title="Recent responses">
            {recent.length === 0 ? (
                <p className="text-sm text-muted-foreground">No responses in this period.</p>
            ) : (
                <ul className="divide-y divide-border">
                    {recent.map((response) => (
                        <li key={response.id} className="flex items-baseline gap-3 py-2 text-sm">
                            <span className="w-10 shrink-0 font-semibold tabular-nums">{scoreLabel(response)}</span>
                            <span className="min-w-0 flex-1">
                                <Link
                                    to="/w/$workspaceId/surveys/$surveyId/responses"
                                    params={{ workspaceId, surveyId: response.surveyId }}
                                    search={respondentSearch(response)}
                                    className="font-medium hover:underline"
                                >
                                    {response.surveyName}
                                </Link>
                                {response.comment ? (
                                    <span className="block truncate text-muted-foreground">{response.comment}</span>
                                ) : null}
                            </span>
                            <span className="shrink-0 text-muted-foreground">{timeAgo(response.submittedAt)}</span>
                        </li>
                    ))}
                </ul>
            )}
        </SectionCard>
    );
}
